import dayjs from 'dayjs';
import {
    AcceptedIcon,
    ExpiredIcon,
    RejectedIcon,
    WaitingForAckIcon,
    WaitingForResponseIcon,
    WarningIcon,
} from '../../utils/icons';
import {Colors} from '../../utils/style/colors';
import {formatDateTime} from '../../utils/time';
import {findDeviceForAlarm, findDeviceForV4Id} from '../../utils/deviceMerge';

// A V4 assistance request mapped onto this page (utils/v4AssistanceRequest).
export const isV4Request = (ar) => !!ar?.get?.('v4');

// Only a V3 request (numeric id) has a V3 record to load; a V4 one is a uuid.
export const hasV3Record = (id) => /^\d+$/.test(String(id ?? ''));

// A V4 request may carry no name; a V3 one renders whatever it has, as before.
export const requestTitle = (ar, name, t) =>
    (name || !isV4Request(ar) ? name : t('assistanceRequest'));

// The source V4 alarm, as the V4 alarm close takes it.
export const v4AlarmToClose = (ar, notes) => ar?.get?.('v4Alarm')?.set('notes', notes || '') || null;

// The V4 close watcher (takeLeading) drops a close sent while another runs, so V4 requests close as one batch.
export function splitForClosing(requests, notes) {
    const v3 = [];
    const v4Alarms = [];
    requests.forEach((ar) => {
        if (!isV4Request(ar)) {
            v3.push(ar);
            return;
        }
        const alarm = v4AlarmToClose(ar, notes);
        if (alarm) v4Alarms.push(alarm);
    });
    return {v3, v4Alarms};
}

// V4 device ids are uuids, and the dedup may keep the V3 row with the uuid overlaid: match id OR uuid.
export function requestDevice(devices, ar, v4OnlyDevices) {
    if (!isV4Request(ar)) return devices.find(d => d.id === ar.deviceId);
    const alarm = ar.get('v4Alarm');
    return (alarm && findDeviceForAlarm(devices, alarm)) || findDeviceForV4Id(devices, v4OnlyDevices, ar.get('deviceId'));
}

// A receiver names only its V4 uuid, which a migrated device's V3 row doesn't carry.
export function recipientDevice(devices, ar, response, v4OnlyDevices) {
    if (!isV4Request(ar)) return devices.find(d => +d.id === +response.device_id);
    return findDeviceForV4Id(devices, v4OnlyDevices, response.device_id);
}

// A V4 request may be raised by a user or the platform, so it falls back to the origin's name.
export const triggeredByName = (ar, device, t) =>
    device?.getName(t) || (isV4Request(ar) && ar.get('originName')) || ar.deviceName || '[?]';

// V3 guesses an unanswered recipient lost after 10 minutes; V4 reports a failed delivery itself.
export const isExpired = (ar, now = dayjs()) =>
    !isV4Request(ar) && ar.isOpen && now.diff(ar.date) > 10 * 60 * 1000;

export function recipientStatus(ar, r, device, expired, t) {
    const v4 = isV4Request(ar);
    let time = '';
    let tooltip = t('waitingForAcknowledgement');
    let color = Colors.gray30;
    let Icon = WaitingForAckIcon;
    let canExpire = true;
    if (r.acknowledged > 0) {
        time = formatDateTime(dayjs.unix(r.acknowledged), {seconds: true});
        Icon = WaitingForResponseIcon;
        tooltip = t('waitingForResponse');
        if (r.response_time > 0) {
            Icon = r.response ? AcceptedIcon : RejectedIcon;
            time = formatDateTime(dayjs.unix(r.response_time), {seconds: true});
            color = r.response ? Colors.success : Colors.danger;
            tooltip = r.response ? t('accepted') : t('rejected');
            canExpire = false;
        }
    }
    if ((canExpire && expired) || (v4 && r.failed)) {
        Icon = ExpiredIcon;
        time = t('neverAcknowledged');
        tooltip = t('neverAcknowledged');
    }
    // The version gate reads V3 app rows only; a V4 receiver got the request.
    if (!v4 && device && !device.canReceiveAssistanceRequests) {
        Icon = WarningIcon;
        color = Colors.warning;
        time = t('neverAcknowledged');
        tooltip = t('versionIncompatibleForFeature');
    }
    return {Icon, color, time, tooltip};
}
